<template>
  <p class="my-4 text-sm text-neutral-550">{{ wt('coordination', 'description') }}</p>

  <p v-if="!disciplines.length" class="mt-8 text-neutral-550">{{ wt('coordination', 'empty') }}</p>
  <template v-else>
    <div class="mt-8">
      <label v-for="discipline in disciplines" :key="discipline.role" class="coordination-row cursor-pointer">
        <input
          type="checkbox"
          class="relative h-6 w-6 shrink-0"
          :checked="!excluded.includes(discipline.role)"
          @change="toggle(discipline.role)" />
        <span class="min-w-0 grow">{{ wtGeneral('roleParts', discipline.role) }}</span>
        <span v-if="discipline.missing" class="text-sm text-neutral-450">{{ wt('coordination', 'missing') }}</span>
        <span v-else-if="discipline.stored" class="text-sm text-neutral-450">
          {{ fillPlaceholders(wt('coordination', 'stored'), { date: formatTimestamp(discipline.calculatedAt) }) }}
        </span>
        <span class="w-24 shrink-0 text-right" :class="{ 'text-neutral-450': excluded.includes(discipline.role) }">
          {{ formatHours(discipline.hours, 0) }} h
        </span>
      </label>
    </div>
    <!-- Same total box as the Grundleistung row on the Leistung page -->
    <div class="mt-4 flex items-center justify-between gap-4 border border-neutral-300 bg-neutral-35 p-5">
      <span>{{ wt('coordination', 'base') }}</span>
      <span class="whitespace-nowrap text-lg">{{ formatHours(baseHours, 0) }} h</span>
    </div>

    <h4 class="mt-10 pb-4 text-base">{{ wt('coordination', 'percent') }}</h4>
    <div class="coordination-slider">
      <div class="coordination-range relative min-w-0 flex-1">
        <div class="mb-1 flex justify-between text-neutral-550">
          <span>0 %</span>
          <span>{{ maximum }} %</span>
        </div>
        <div class="relative">
          <div class="coordination-markers" :style="{ '--marker-gaps': maximum }" aria-hidden="true">
            <span
              v-for="step in markers"
              :key="step"
              :class="{ active: step <= percent }"
              :style="{ left: `${(step / maximum) * 100}%` }" />
          </div>
          <RangeInput
            :modelValue="percent"
            :min="0"
            :max="maximum"
            :step="1"
            :ariaLabel="wt('coordination', 'percent')"
            @update:modelValue="setPercent" />
          <span class="slider-value" :style="{ left: thumbPosition }">{{ percent }} %</span>
        </div>
      </div>
      <div class="coordination-input-group">
        <button
          type="button"
          class="adjust-value-button"
          :disabled="percent <= 0"
          :aria-label="`${wt('coordination', 'percent')} −1 %`"
          @click="setPercent(percent - 1)">
          <IconMinus class="h-4 w-4" stroke="2" />
        </button>
        <input
          :value="percent"
          type="number"
          min="0"
          :max="maximum"
          step="1"
          :aria-label="wt('coordination', 'percent')"
          class="core-form-input mb-0 w-14 text-center text-primary-dark md:w-16"
          @change="setPercent(($event.target as HTMLInputElement).value)" />
        <span class="ml-1 text-sm text-neutral-550">%</span>
        <button
          type="button"
          class="adjust-value-button"
          :disabled="percent >= maximum"
          :aria-label="`${wt('coordination', 'percent')} +1 %`"
          @click="setPercent(percent + 1)">
          <IconPlus class="h-4 w-4" stroke="2" />
        </button>
      </div>
    </div>

    <h4 class="mt-10 pb-4 text-base">{{ wt('coordination', 'justification') }}</h4>
    <textarea
      class="core-form-textarea mt-0"
      rows="3"
      maxlength="5000"
      :value="String(modelValue?.coordinationJustification ?? '')"
      @change="patch({ coordinationJustification: ($event.target as HTMLTextAreaElement).value })" />
  </template>

  <div class="mt-8 flex items-center justify-between gap-4 border border-neutral-300 bg-neutral-35 p-5">
    <div>
      <div class="font-medium text-neutral-700">{{ wt('coordination', 'result') }}</div>
      <div class="mt-1 text-sm text-neutral-450">
        {{ zeroReason ?? `${formatHours(baseHours, 0)} h × ${percent} %` }}
      </div>
    </div>
    <span class="whitespace-nowrap text-xl font-bold" :class="zeroReason ? 'text-neutral-450' : 'text-primary-default'">
      {{ formatHours(calculator.getEstimatedTotalHours, 0) }} h
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { IconMinus, IconPlus } from '@tabler/icons-vue'
import RangeInput from '@/components/partials/inputs/RangeInput.vue'
import type { CoordinationBaseRole } from '@arcexperts/core-types'
import type { NestedObject } from '@/types'
import { coordinationZeroReason, useCoordination } from '@/helpers/coordination'
import { useWidgetText } from '@/helpers/useWidgetText'
import { fillPlaceholders } from '@/helpers/text-placeholders'
import { formatHours, formatTimestamp } from '@/helpers/format'

const props = defineProps<{ modelValue: NestedObject | null; calculator: { getEstimatedTotalHours: number } }>()
const emit = defineEmits<{ 'update:modelValue': [value: NestedObject] }>()
const { disciplines, settings } = useCoordination()
const { wt } = useWidgetText('widget-texts-project-setup', 'SPECIALIST_COORDINATION')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')

const maximum = computed(() => settings.value?.maxPercent ?? 20)
const defaultPercent = computed(() => settings.value?.defaultPercent ?? 5)
// The socket caps a saved value at a lowered maximum, so the form shows the same value.
const percent = computed(() =>
  Math.min(
    (props.modelValue?.coordinationSurchargePercent as number | null | undefined) ?? defaultPercent.value,
    maximum.value
  )
)
const excluded = computed(() => (props.modelValue?.coordinationExcludedRoles as CoordinationBaseRole[]) ?? [])
const selected = computed(() => disciplines.value.filter((entry) => !excluded.value.includes(entry.role)))
const baseHours = computed(() => selected.value.reduce((sum, entry) => sum + entry.hours, 0))
// Replaces the formula in the result box, so no notice has to appear elsewhere in the form.
const zeroReason = computed(() => coordinationZeroReason(percent.value, selected.value.length, wt))
// One marker per 1 % step, like the task sliders on the Beschrieb page.
const markers = computed(() => Array.from({ length: maximum.value + 1 }, (_, index) => index))
// The thumb is 20px wide, so its centre runs from 10px to (width - 10px).
const thumbPosition = computed(() => {
  const progress = percent.value / maximum.value
  return `calc(${progress * 100}% + ${10 - progress * 20}px)`
})

function patch(value: NestedObject) {
  emit('update:modelValue', { ...props.modelValue, ...value })
}
function toggle(role: CoordinationBaseRole) {
  patch({
    coordinationExcludedRoles: excluded.value.includes(role)
      ? excluded.value.filter((entry) => entry !== role)
      : [...excluded.value, role],
  })
}
function setPercent(value: string | number) {
  const number = Number(value)
  if (!Number.isFinite(number)) return
  patch({ coordinationSurchargePercent: Math.max(0, Math.min(maximum.value, Math.round(number))) })
}
</script>

<style lang="postcss" scoped>
/* The right padding matches the total box below (1px border + p-5), so the hours line up. */
.coordination-row {
  @apply flex items-center gap-4 border-t border-neutral-300 py-4 pr-[21px] text-neutral-650 first:border-t-0;
}

/* Same slider and stepper look as the task sliders on the Beschrieb page */
.coordination-slider {
  @apply flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-5;
}

.coordination-input-group {
  @apply flex w-36 items-center justify-end self-end md:w-40 lg:mt-5 lg:self-start;
}

.coordination-range :deep(.core-input-range-wrapper) {
  @apply m-0;
}

.coordination-range :deep(.core-input-range) {
  @apply relative z-20 mt-0 h-5;
  margin-bottom: 1.75rem;
  background-size: calc(100% - 20px) 4px;
  background-position: center;
  background-repeat: no-repeat;
}

.coordination-range :deep(.core-input-range::-webkit-slider-thumb) {
  @apply -mt-2 h-5 w-5 border-2 shadow;
  border-color: var(--color-primary-dark);
}

.coordination-range :deep(.core-input-range::-moz-range-thumb) {
  @apply h-5 w-5 border-2 shadow;
  border-color: var(--color-primary-dark);
}

.coordination-range :deep(.core-input-range::-webkit-slider-runnable-track) {
  @apply h-1 rounded-full;
}

.coordination-range :deep(.core-input-range::-moz-range-track) {
  @apply h-1 rounded-full;
}

/* Up to 21 markers: on narrow screens they shrink so a gap of at least 4px stays between them. */
.coordination-markers {
  @apply pointer-events-none absolute z-10 h-0;
  container-type: inline-size;
  top: 10px;
  left: 10px;
  right: 10px;

  span {
    @apply absolute rounded-full bg-neutral-300;
    width: 12px;
    width: clamp(4px, round(down, 100cqw / var(--marker-gaps) - 4px, 2px), 12px);
    aspect-ratio: 1;
    transform: translate(-50%, -50%);

    &.active {
      background-color: var(--color-primary-dark);
    }
  }
}

.slider-value {
  @apply absolute top-6 -translate-x-1/2 whitespace-nowrap text-sm text-neutral-550;
}

.adjust-value-button {
  @apply inline-flex h-10 w-8 cursor-pointer items-center justify-center text-neutral-550 transition-colors hover:text-black disabled:cursor-default disabled:opacity-40 lg:w-10;
}

input[type='number'] {
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
